<template>
  <div class="report-editor">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/" class="breadcrumb">
      <el-breadcrumb-item :to="{ path: '/main/report-templates' }">
        模板列表
      </el-breadcrumb-item>
      <el-breadcrumb-item>
        {{ id ? "编辑模板" : "新建模板" }}
      </el-breadcrumb-item>
    </el-breadcrumb>

    <el-card class="editor-container" v-loading="loading">
      <template v-if="!report">
        <!-- 加载骨架屏 -->
        <div style="text-align: center; padding: 50px">
          <el-skeleton :rows="5" animated />
        </div>
      </template>
      <template v-else>
        <!-- 报表标题 -->
        <div class="editor-header">
          <div class="left">
            <div class="template-name-wrapper">
              <span class="template-name-label">模板名称：</span>
              <el-input
                v-model="template_name"
                placeholder="请输入模板名称"
                class="template-name-input"
              />
            </div>
          </div>
        </div>

        <!-- 报表类型 -->
        <div class="report-type-wrapper">
          <span class="report-type-label">报表类型：</span>
          <el-select
            v-model="template_type"
            placeholder="请选择报表类型"
            class="report-type-select"
          >
            <el-option label="基础模板" value="basic" />
            <el-option label="高级模板" value="advanced" />
          </el-select>
        </div>

        <!-- 所属旗县 -->
        <div class="report-type-wrapper" style="margin-top: 20px">
          <span class="report-type-label">所属旗县：</span>
          <el-select
            v-model="sub_department_id"
            placeholder="请选择所属旗县"
            class="report-type-select"
            clearable
          >
            <el-option
              v-for="dept in sub_departments"
              :key="dept.id"
              :label="dept.name"
              :value="dept.id"
            />
          </el-select>
        </div>

        <!-- 报表标题 -->
        <el-input
          v-model="report.title"
          placeholder="请输入报表标题"
          class="report-title"
        />

        <!-- 报表描述 -->
        <el-input
          v-model="report.description"
          placeholder="请输入报表描述"
          class="report-description"
          type="textarea"
          :rows="3"
          resize="none"
          :autosize="{ minRows: 2, maxRows: 4 }"
        />

        <!-- 分组管理区域 -->
        <div class="group-management">
          <div class="group-management-header">
            <h3>分组管理</h3>
            <el-button type="primary" size="small" @click="showNewGroupDialog">
              <el-icon><CirclePlus /></el-icon>新建分组
            </el-button>
          </div>
          <div class="group-tree">
            <el-tree
              :data="groups"
              :props="{
                label: 'name',
                children: 'children'
              }"
              node-key="id"
              default-expand-all
            >
              <template #default="{ node, data }">
                <div class="group-tree-node">
                  <span>{{ node.label }}</span>
                  <div class="group-tree-actions">
                    <el-button type="text" @click="addSubGroup(data)">
                      <el-icon><CirclePlus /></el-icon>
                    </el-button>
                    <el-button type="text" @click="editGroup(data)">
                      <el-icon><Edit /></el-icon>
                    </el-button>
                    <el-button type="text" @click="deleteGroup(data)">
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </div>
                </div>
              </template>
            </el-tree>
          </div>
        </div>

        <!-- 行配置区域 -->
        <div class="row-configuration">
          <div class="row-configuration-header">
            <h3>行配置</h3>
            <el-button type="primary" size="small" @click="showNewRowDialog">
              <el-icon><CirclePlus /></el-icon>新建行配置
            </el-button>
          </div>
          <div class="row-tree">
            <el-tree
              :data="rowConfigs"
              :props="{
                label: 'name',
                children: 'children'
              }"
              node-key="id"
              default-expand-all
            >
              <template #default="{ node, data }">
                <div class="row-tree-node">
                  <span>{{ node.label }}</span>
                  <div class="row-tree-actions">
                    <el-button type="text" @click="addSubRow(data)">
                      <el-icon><CirclePlus /></el-icon>
                    </el-button>
                    <el-button type="text" @click="editRow(data)">
                      <el-icon><Edit /></el-icon>
                    </el-button>
                    <el-button type="text" @click="deleteRow(data)">
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </div>
                </div>
              </template>
            </el-tree>
          </div>
        </div>

        <!-- 问题列表 -->
        <div class="question-list">
          <div
            v-for="(question, questionIndex) in report.questions"
            :key="questionIndex"
            class="question-item"
          >
            <!-- 第一行：序号和问题类型 -->
            <div class="question-meta">
              <div class="question-number">
                {{ (questionIndex + 1).toString().padStart(2, "0") }}
              </div>
              <div class="question-type-container">
                <el-select
                  v-model="question.type"
                  placeholder="选择类型"
                  class="question-type"
                >
                  <el-option label="数值输入" value="number" />
                  <el-option label="文本输入" value="text" />
                  <el-option label="下拉框" value="select" />
                  <el-option label="单选" value="radio" />
                  <el-option label="多选" value="checkbox" />
                  <el-option label="智能计算" value="calculation" />
                </el-select>
                <el-select
                  v-model="question.group"
                  placeholder="选择分组"
                  class="question-group"
                  clearable
                  @change="handleGroupChange($event, question)"
                >
                  <el-option
                    v-for="group in getGroupOptions()"
                    :key="group.value"
                    :label="group.label"
                    :value="group.value"
                  />
                  <el-option label="新建分组" value="__new__" />
                </el-select>
                <el-button type="text" @click="removeQuestion(questionIndex)">
                  <el-icon :size="18" color="#a8abb2"><Close /></el-icon>
                </el-button>
              </div>
            </div>

            <!-- 第二行：问题内容 -->
            <div>
              <div style="display: flex; align-items: center; gap: 10px">
                <el-input
                  v-model="question.title"
                  :placeholder="`请输入字段名称`"
                  class="question-content"
                  :class="{ 'error-field': hasError(questionIndex, 'title') }"
                />
                <el-checkbox
                  v-if="
                    question.type === 'number' ||
                    question.type === 'calculation'
                  "
                  v-model="question.showTotal"
                  >显示合计行</el-checkbox
                >
                <el-checkbox v-model="question.required">必填</el-checkbox>
              </div>
              <div v-if="hasError(questionIndex, 'title')" class="error-message">
                {{ getError(questionIndex, "title") }}
              </div>
            </div>

            <!-- 根据问题类型显示不同选项 -->
            <div
              v-if="
                question.type === 'radio' ||
                question.type === 'checkbox' ||
                question.type === 'select'
              "
              class="options-area"
            >
              <el-select
                v-if="question.type === 'select'"
                size="large"
                placeholder="请选择"
                style="pointer-events: none; margin-bottom: 10px; width: 400px"
              />

              <div
                v-for="(option, optIndex) in question.options"
                :key="optIndex"
                class="option-item"
              >
                <el-input
                  v-model="question.options[optIndex]"
                  :placeholder="`选项 ${optIndex + 1}`"
                  size="large"
                  :class="{
                    'error-option': hasError(questionIndex, 'option', optIndex),
                  }"
                >
                  <template #prefix>
                    <el-radio
                      v-if="question.type === 'radio'"
                      :model-value="false"
                      style="pointer-events: none"
                    />
                    <el-checkbox
                      v-else-if="question.type === 'checkbox'"
                      :model-value="false"
                      style="pointer-events: none"
                    />
                  </template>
                </el-input>
                <el-button type="text" @click="removeOption(questionIndex, optIndex)">
                  <el-icon :size="18" color="#a8abb2"><Close /></el-icon>
                </el-button>
              </div>
              <el-button type="text" @click="addOption(questionIndex)">
                <el-icon :size="16" style="margin-right: 10px"
                  ><CirclePlus /></el-icon
                >添加选项
              </el-button>
              <div v-if="hasError(questionIndex, 'options')" class="error-message">
                {{ getError(questionIndex, "options") }}
              </div>
            </div>

            <!-- 数字输入类型 -->
            <div
              v-else-if="question.type === 'number'"
              class="number-input-area"
            >
              <!-- 数据校验选项 -->
              <div class="validation-options" v-if="question.type === 'number'">
                <el-divider content-position="left">数据校验</el-divider>

                <!-- 周期数据校验 -->
                <div class="validation-item">
                  <el-select
                    v-model="question.periodicValidation"
                    placeholder="周期校验"
                    clearable
                    style="width: 100%; margin-bottom: 10px"
                  >
                    <el-option label="周期递增" value="increasing" />
                    <el-option label="周期递减" value="decreasing" />
                  </el-select>
                </div>

                <!-- 取值范围校验 -->
                <div class="validation-item">
                  <el-divider content-position="left">取值范围</el-divider>
                  <div style="display: flex; gap: 10px; align-items: center">
                    <el-input-number
                      v-model="question.rangeValidation.min"
                      placeholder="最小值"
                      style="flex: 1"
                    />
                    <span>至</span>
                    <el-input-number
                      v-model="question.rangeValidation.max"
                      placeholder="最大值"
                      style="flex: 1"
                    />
                  </div>
                </div>

                <!-- 字段间数据校验 -->
                <div class="validation-item">
                  <el-select
                    v-model="question.fieldValidation.targetQuestion"
                    placeholder="选择参照字段"
                    clearable
                    style="width: 100%; margin-bottom: 5px"
                    :disabled="!hasOtherNumberQuestions(questionIndex)"
                  >
                    <el-option
                      v-for="item in getOtherNumberQuestions(questionIndex)"
                      :key="item.originalIndex"
                      :label="`字段${item.originalIndex + 1}${
                        item.question.group ? '-' + item.question.group : ''
                      }${item.question.title ? '-' + item.question.title : ''}`"
                      :value="item.originalIndex"
                      :disabled="item.disabled"
                    />
                  </el-select>

                  <div
                    v-if="question.fieldValidation.targetQuestion !== null"
                    style="display: flex; gap: 10px"
                  >
                    <el-select
                      v-model="question.fieldValidation.operator"
                      placeholder="选择关系"
                      style="flex: 1"
                    >
                      <el-option label="大于所选字段倍数" value="gt" />
                      <el-option label="大于等于所选字段倍数" value="gte" />
                      <el-option label="等于所选字段倍数" value="eq" />
                      <el-option label="小于所选字段倍数" value="lt" />
                      <el-option label="小于等于所选字段倍数" value="lte" />
                    </el-select>

                    <el-input-number
                      v-model="question.fieldValidation.multiplier"
                      :min="0"
                      :step="0.1"
                      placeholder="倍数"
                      style="flex: 1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!-- 智能计算类型 -->
            <div
              v-else-if="question.type === 'calculation'"
              class="calculation-area"
            >
              <el-divider content-position="left">计算设置</el-divider>
              <div class="calculation-settings">
                <!-- 展示样式选择 -->
                <div class="validation-item" style="margin-bottom: 15px;">
                  <el-divider content-position="left">展示样式</el-divider>
                  <el-select
                    v-model="question.displayStyle"
                    placeholder="选择展示样式"
                    style="width: 100%"
                  >
                    <el-option label="默认" value="default" />
                    <el-option label="自动取整" value="round" />
                    <el-option label="百分数" value="percentage" />
                    <el-option label="千分数" value="permille" />
                  </el-select>
                </div>

                <!-- 计算表达式区域 -->
                <div class="calculation-expression">
                  <div v-for="(_, fieldIndex) in question.calculation.fields" :key="fieldIndex" class="expression-item">
                    <!-- 字段选择 -->
                    <el-select
                      v-model="question.calculation.fields[fieldIndex]"
                      placeholder="选择字段"
                      style="width: 200px"
                      :class="{
                        'error-field': hasError(fieldIndex, 'calculation', 'fields')
                      }"
                    >
                      <el-option
                        v-for="item in getOtherNumberQuestions(questionIndex)"
                        :key="item.originalIndex"
                        :label="`字段${item.originalIndex + 1}${
                          item.question.group ? '-' + item.question.group : ''
                        }${item.question.title ? '-' + item.question.title : ''}`"
                        :value="item.originalIndex"
                        :disabled="item.disabled"
                      />
                    </el-select>

                    <!-- 运算符选择（除了最后一个字段外都需要） -->
                    <template v-if="fieldIndex < question.calculation.fields.length - 1">
                      <el-select
                        v-model="question.calculation.operators[fieldIndex]"
                        placeholder="运算符"
                        style="width: 100px; margin: 0 10px"
                      >
                        <el-option label="加 (+)" value="+" />
                        <el-option label="减 (-)" value="-" />
                        <el-option label="乘 (×)" value="*" />
                        <el-option label="除 (÷)" value="/" />
                      </el-select>
                    </template>

                    <!-- 删除按钮 -->
                    <el-button
                      type="text"
                      @click="removeCalculationField(questionIndex, fieldIndex)"
                      :disabled="question.calculation.fields.length <= 2"
                    >
                      <el-icon><Delete /></el-icon>
                    </el-button>
                  </div>

                  <!-- 添加字段按钮 -->
                  <el-button
                    type="text"
                    @click="addCalculationField(questionIndex)"
                    style="margin-top: 10px"
                  >
                    <el-icon><CirclePlus /></el-icon>
                    添加字段
                  </el-button>
                </div>

                <!-- 错误信息显示 -->
                <div
                  v-if="hasError(questionIndex, 'calculation', 'fields')"
                  class="error-message"
                >
                  {{ getError(questionIndex, "calculation", "fields") }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="action-buttons">
          <el-button type="primary" @click="addQuestion"> 添加问题 </el-button>
          <el-button type="success" @click="saveReport"> 保存报表 </el-button>
        </div>
      </template>
    </el-card>

    <!-- 新建分组对话框 -->
    <el-dialog
      v-model="newGroupDialog.visible"
      title="新建分组"
      width="30%"
      @close="cancelNewGroup"
    >
      <el-form>
        <el-form-item label="父级分组">
          <el-select
            v-model="newGroupDialog.parentGroup"
            placeholder="选择父级分组"
            clearable
          >
            <el-option
              v-for="group in getGroupOptions()"
              :key="group.value"
              :label="group.label"
              :value="group.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="分组名称">
          <el-input
            v-model="newGroupDialog.name"
            @keyup.enter="confirmNewGroup"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="cancelNewGroup">取消</el-button>
        <el-button type="primary" @click="confirmNewGroup">确认</el-button>
      </template>
    </el-dialog>

    <!-- 新建行配置对话框 -->
    <el-dialog
      v-model="newRowDialog.visible"
      :title="newRowDialog.editingRow ? '编辑行配置' : '新建行配置'"
      width="30%"
      @close="cancelNewRow"
    >
      <el-form>
        <el-form-item label="父级行">
          <el-select
            v-model="newRowDialog.parentRow"
            placeholder="选择父级行"
            clearable
          >
            <el-option
              v-for="row in getRowOptions()"
              :key="row.value"
              :label="row.label"
              :value="row.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="行名称">
          <el-input
            v-model="newRowDialog.name"
            @keyup.enter="confirmNewRow"
          />
        </el-form-item>
        <el-form-item label="行类型">
          <el-select v-model="newRowDialog.type" placeholder="选择行类型">
            <el-option label="数据行" value="data" />
            <el-option label="小计行" value="subtotal" />
          </el-select>
        </el-form-item>
        <el-form-item label="计算规则" v-if="newRowDialog.type !== 'data'">
          <el-select v-model="newRowDialog.calculationRule" placeholder="选择计算规则">
            <el-option label="求和" value="sum" />
            <el-option label="平均值" value="average" />
            <el-option label="最大值" value="max" />
            <el-option label="最小值" value="min" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="cancelNewRow">取消</el-button>
        <el-button type="primary" @click="confirmNewRow">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
/* eslint-disable */
import { Delete, Search, Close, CirclePlus, Edit } from "@element-plus/icons-vue";
import {
  createTemplate,
  updateTemplate,
  getTemplateById,
} from "@/api/report-system/templete";
import { getSubDepartments } from "@/api/user-management/sub_department"; // 添加部门API导入

export default {
  name: "ReportEditor",
  props: {
    id: {
      type: String,
      default: null,
    },
  },
  setup() {
    return {
      Delete,
      Search,
      Close,
      CirclePlus,
      Edit,
    };
  },
  components: {
    Delete,
    Search,
    Close,
    CirclePlus,
    Edit,
  },
  data() {
    return {
      loading: true,
      template_name: "",
      template_type: "basic",
      sub_department_id: null, // 添加部门ID字段
      sub_departments: [], // 添加部门列表
      report: null,
      validationErrors: {
        questions: [],
      },
      groups: [], // 存储分组树形结构
      newGroupDialog: {
        visible: false,
        name: "",
        question: null,
        parentGroup: null, // 新增：父分组
        editingGroup: null,
      },
      rowConfigs: [], // 存储行配置的树形结构
      newRowDialog: {
        visible: false,
        name: "",
        parentRow: null,
        editingRow: null,
        type: "data", // 行类型：data(数据行), total(合计行), subtotal(小计行)
        calculationRule: "sum", // 计算规则：sum, average, max, min
      },
    };
  },
  methods: {
    // 初始化新问题
    addQuestion() {
      this.report.questions.push({
        title: "",
        type: "number",
        options: ["", ""],
        group: "",
        required: true,
        displayStyle: "default",
        periodicValidation: null,
        showTotal: false,
        rangeValidation: {
          min: null,
          max: null,
        },
        fieldValidation: {
          targetQuestion: null,
          operator: null,
          multiplier: 1,
        },
        calculation: {
          fields: [null, null], // 初始化为两个空字段
          operators: [null], // 初始化为一个空运算符
        },
      });
    },

    // 检查字段是否被引用
    isFieldReferenced(fieldIndex) {
      const references = [];

      // 检查智能计算引用
      this.report.questions.forEach((q, i) => {
        if (q.type === "calculation" && q.calculation) {
          // 检查field1是否存在且等于fieldIndex
          if (
            q.calculation.fields &&
            q.calculation.fields.length > 0 &&
            q.calculation.fields.includes(fieldIndex)
          ) {
            references.push(
              `智能计算字段"${q.title || "未命名字段" + (i + 1)}"的字段${fieldIndex + 1}`
            );
          }
        }

        // 检查字段校验引用
        if (
          q.type === "number" &&
          q.fieldValidation.targetQuestion === fieldIndex
        ) {
          references.push(`字段"${q.title || "未命名字段" + (i + 1)}"`);
        }
      });

      return references.length > 0 ? references : false;
    },

    // 删除问题
    removeQuestion(index) {
      if (this.report.questions.length <= 1) {
        this.$message.warning("至少需要保留一个问题");
        return;
      }

      // 检查该字段是否被引用
      const references = this.isFieldReferenced(index);
      if (references) {
        this.$message.error(`该字段被${references.join("、")}引用，无法删除`);
        return;
      }

      this.report.questions.splice(index, 1);
    },

    // 添加选项
    addOption(questionIndex) {
      this.report.questions[questionIndex].options.push("");
    },

    // 删除选项
    removeOption(questionIndex, optionIndex) {
      this.report.questions[questionIndex].options.splice(optionIndex, 1);
    },

    // 获取其他数值类型问题及其原始索引
    getOtherNumberQuestions(currentIndex) {
      // 检查字段是否被其他计算字段引用
      const isReferencedBy = (sourceIndex, targetIndex, visited = new Set()) => {
        // 防止无限递归
        if (visited.has(targetIndex)) return false;
        visited.add(targetIndex);

        // 检查索引是否有效
        if (targetIndex < 0 || targetIndex >= this.report.questions.length) return false;

        const targetQuestion = this.report.questions[targetIndex];
        if (!targetQuestion || targetQuestion.type !== 'calculation') return false;

        // 确保calculation对象存在且fields是数组
        if (!targetQuestion.calculation || !Array.isArray(targetQuestion.calculation.fields)) return false;

        // 检查直接引用
        if (targetQuestion.calculation.fields.includes(sourceIndex)) {
          return true;
        }

        // 检查间接引用
        return targetQuestion.calculation.fields.some(fieldIndex => {
          // 确保fieldIndex是有效的数字
          if (typeof fieldIndex !== 'number' || fieldIndex < 0 || fieldIndex >= this.report.questions.length) {
            return false;
          }
          return isReferencedBy(sourceIndex, fieldIndex, new Set(visited));
        });
      };

      return this.report.questions
        .map((q, i) => ({
          question: q,
          originalIndex: i,
        }))
        .filter(
          (item) => {
            // 排除当前字段
            if (item.originalIndex === currentIndex) return false;

            // 只允许选择数值类型和计算类型字段
            if (item.question.type !== "number" && item.question.type !== "calculation") return false;

            // 如果当前字段是计算字段，检查是否会造成循环引用
            const currentQuestion = this.report.questions[currentIndex];
            if (currentQuestion && currentQuestion.type === 'calculation') {
              // 检查选择这个字段是否会造成循环引用
              if (isReferencedBy(currentIndex, item.originalIndex)) return false;
            }

            return true;
          }
        )
        .map(item => ({
          ...item,
          disabled: false
        }));
    },

    // 检查是否有足够其他数值类型问题(智能计算需要至少2个)
    hasOtherNumberQuestions(currentIndex) {
      const otherNumberQuestions = this.getOtherNumberQuestions(currentIndex);
      const currentQuestion = this.report.questions[currentIndex];

      // 如果是智能计算类型，需要至少2个其他数值字段
      if (currentQuestion.type === "calculation") {
        return otherNumberQuestions.length >= 2;
      }

      // 其他类型只需要1个
      return otherNumberQuestions.length > 0;
    },

    // 修改验证逻辑
    validateForm() {
      this.validationErrors.questions = [];
      let isValid = true;

      this.report.questions.forEach((q, i) => {
        const errors = [];
        
        // 验证字段名称
        if (!q.title || q.title.trim() === "") {
          errors.push({ type: "title", message: "字段名称不能为空" });
          isValid = false;
        }

        // 验证选项类字段
        if (["radio", "checkbox", "select"].includes(q.type)) {
          if (q.options.length < 2) {
            errors.push({ type: "options", message: "需要至少2个选项" });
            isValid = false;
          }

          q.options.forEach((opt, j) => {
            if (!opt || opt.trim() === "") {
              errors.push({
                type: "option",
                index: j,
                message: "选项不能为空",
              });
              isValid = false;
            }
          });
        }

        // 验证计算字段
        if (q.type === "calculation") {
          // 验证字段数量
          if (!q.calculation.fields || q.calculation.fields.length < 2) {
            errors.push({
              type: "calculation",
              field: "fields",
              message: "需要至少选择2个字段",
            });
            isValid = false;
          } else {
            // 验证每个字段都已选择
            q.calculation.fields.forEach((field, index) => {
              if (field === null || field === undefined) {
                errors.push({
                  type: "calculation",
                  field: "fields",
                  message: `第${index + 1}个字段不能为空`,
                });
                isValid = false;
              }
            });
          }

          // 验证运算符
          if (!q.calculation.operators || q.calculation.operators.length < 1) {
            errors.push({
              type: "calculation",
              field: "operators",
              message: "需要至少选择1个运算符",
            });
            isValid = false;
          } else {
            // 验证运算符数量是否正确（应比字段数少1）
            if (q.calculation.operators.length !== q.calculation.fields.length - 1) {
              errors.push({
                type: "calculation",
                field: "operators",
                message: "运算符数量不正确",
              });
              isValid = false;
            }
            // 验证每个运算符都已选择
            q.calculation.operators.forEach((operator, index) => {
              if (operator === null || operator === undefined) {
                errors.push({
                  type: "calculation",
                  field: "operators",
                  message: `第${index + 1}个运算符不能为空`,
                });
                isValid = false;
              }
            });
          }
        }

        if (errors.length > 0) {
          this.validationErrors.questions[i] = errors;
        }
      });

      return isValid;
    },

    // 保存报表
    async saveReport() {
      if (!this.validateForm()) {
        return;
      }

      try {
        const saveData = {
          name: this.template_name || "未命名模板",
          template_type: this.template_type || "basic",
          // 必须归一成数字：后端 input.SubDepartmentID 已改为 *uint 做部分更新，
          // 传 null 会被当成"未提供该字段"而保留原科室，清空操作就失效了；
          // 传 "" 则直接报参数错误。显式传 0 才是后端约定的"清空科室关联"。
          // 注意 saveData 下面被创建路径（createTemplate）一并展开复用，所以这里的 0
          // 也会走到创建接口；后端 CreateReportTemplate 同样把 0 归一成无科室（NULL）。
          sub_department_id: Number(this.sub_department_id) || 0,
          content: JSON.stringify({
            title: this.report.title || "未命名报表",
            description: this.report.description,
            questions: this.report.questions,
            groups: this.groups,
            rowConfigs: this.rowConfigs,
          }),
        };

        if (this.id) {
          // 更新已有模板
          await updateTemplate(this.id, saveData);
          this.$message.success("模板更新成功");
        } else {
          // 创建新模板
          const response = await createTemplate({
            ...saveData,
            creator_id: 1, // 这里应该是当前用户ID
          });
          this.$message.success("模板创建成功");
        }
        // 保存成功后跳转回模板列表页
        this.$router.push("/main/report-templates");
      } catch (error) {
        this.$message.error(`保存失败: ${error.message || error}`);
        console.error("保存失败:", error);
      }
    },

    // 加载模板数据
    async loadTemplate() {
      if (!this.id) {
        // 新建模板时使用默认结构
        this.report = {
          title: "",
          description: "",
          questions: [
            {
              title: "",
              type: "number",
              options: ["", ""],
              group: "",
              required: true,
              displayStyle: "default",
              periodicValidation: null,
              showTotal: false,
              rangeValidation: {
                min: null,
                max: null,
              },
              fieldValidation: {
                targetQuestion: null,
                operator: null,
                multiplier: 1,
              },
              calculation: {
                fields: [null, null],
                operators: [null]
              },
            },
          ],
        };
        this.groups = [];
        this.rowConfigs = [];
        this.loading = false;
        return;
      }

      try {
        this.loading = true;
        const { data } = await getTemplateById(this.id);

        // 初始化report结构
        this.report = {
          title: "",
          description: "",
          questions: [],
        };

        if (data.content && data.content.trim() !== "{}") {
          const parsedContent = JSON.parse(data.content);
          
          // 处理历史数据格式
          if (parsedContent.questions) {
            parsedContent.questions = parsedContent.questions.map(question => {
              // 确保每个问题都有 displayStyle 字段
              if (!question.displayStyle) {
                question.displayStyle = "default";
              }
              
              // 转换旧的计算字段格式为新格式
              if (question.type === 'calculation' && question.calculation && !Array.isArray(question.calculation.fields)) {
                question.calculation = {
                  fields: [question.calculation.field1, question.calculation.field2],
                  operators: [question.calculation.operator]
                };
              }
              return question;
            });
          }
          
          // 处理历史分组数据
          if (parsedContent.groups && Array.isArray(parsedContent.groups)) {
            // 检查是否是旧格式（一维数组）
            if (parsedContent.groups.length > 0 && typeof parsedContent.groups[0] === 'string') {
              // 将旧格式转换为新格式
              this.groups = parsedContent.groups.map((groupName, index) => ({
                id: `legacy_${index}`,
                name: groupName,
                children: []
              }));

              // 更新问题的分组ID
              if (parsedContent.questions) {
                parsedContent.questions = parsedContent.questions.map(question => {
                  if (question.group && typeof question.group === 'string') {
                    // 查找对应的新分组ID
                    const groupIndex = parsedContent.groups.indexOf(question.group);
                    if (groupIndex !== -1) {
                      question.group = `legacy_${groupIndex}`;
                    }
                  }
                  return question;
                });
              }
            } else {
              // 已经是新格式，直接使用
              this.groups = parsedContent.groups;
            }
          } else {
            this.groups = [];
          }

          // 如果rangeValidation不存在，则设置为空对象
          parsedContent.questions.forEach((question) => {
            question.rangeValidation = question.rangeValidation || {};
          });

          // 加载行配置
          if (parsedContent.rowConfigs && Array.isArray(parsedContent.rowConfigs)) {
            this.rowConfigs = parsedContent.rowConfigs;
          } else {
            this.rowConfigs = [];
          }

          this.report = {
            ...this.report,
            ...parsedContent,
          };
        }

        this.template_name = data.name || "未命名模板";
        this.template_type = data.template_type || "basic";
        this.sub_department_id = data.sub_department_id; // 设置部门ID
      } catch (error) {
        this.$message.error(`加载模板失败: ${error.message || error}`);
      } finally {
        this.loading = false;
      }
    },

    // 检查数值字段数量是否足够
    checkNumberFields(index) {
      const otherNumberQuestions = this.getOtherNumberQuestions(index);
      if (otherNumberQuestions.length < 2) {
        this.$message.warning("智能计算需要至少2个其他数值字段");
      }
    },

    // 检查是否有错误
    hasError(questionIndex, type, fieldOrIndex) {
      const errors = this.validationErrors.questions[questionIndex] || [];
      return errors.some((err) => {
        if (type === "calculation") {
          return err.type === type && err.field === fieldOrIndex;
        }
        return (
          err.type === type &&
          (fieldOrIndex === undefined || err.index === fieldOrIndex)
        );
      });
    },

    // 获取错误信息
    getError(questionIndex, type, field) {
      const errors = this.validationErrors.questions[questionIndex] || [];
      const error = errors.find((err) => {
        if (type === "calculation") {
          return err.type === type && err.field === field;
        }
        return err.type === type;
      });
      return error ? error.message : "";
    },

    // 验证数字输入
    validateNumber(question) {
      // 这里添加数字验证逻辑
    },

    // 监听分组选择变化
    watch: {
      "report.questions": {
        handler(questions) {
          // 收集所有分组名称
          const groupSet = new Set();
          questions.forEach((q) => {
            if (q.group) {
              groupSet.add(q.group);
            }
          });
          this.groups = Array.from(groupSet);
        },
        deep: true,
      },
    },

    // 获取分组选项（包含层级关系）
    getGroupOptions() {
      const options = [];
      const buildOptions = (groups, prefix = '') => {
        groups.forEach(group => {
          const label = prefix ? `${prefix} / ${group.name}` : group.name;
          options.push({
            label,
            value: group.id
          });
          if (group.children && group.children.length > 0) {
            buildOptions(group.children, label);
          }
        });
      };
      buildOptions(this.groups);
      return options;
    },

    // 处理分组选择变化
    handleGroupChange(value, question) {
      if (value === "__new__") {
        this.newGroupDialog.question = question;
        this.newGroupDialog.visible = true;
      }
    },

    // 显示新建分组对话框
    showNewGroupDialog() {
      this.newGroupDialog = {
        visible: true,
        name: "",
        question: null,
        parentGroup: null
      };
    },

    // 添加子分组
    addSubGroup(parentGroup) {
      this.newGroupDialog = {
        visible: true,
        name: "",
        question: null,
        parentGroup: parentGroup.id
      };
    },

    // 编辑分组
    editGroup(group) {
      // 找到当前分组的父分组
      const findParentGroup = (groups, targetId) => {
        for (const g of groups) {
          if (g.children && g.children.length > 0) {
            for (const child of g.children) {
              if (child.id === targetId) {
                return g.id;
              }
              if (child.children && child.children.length > 0) {
                const found = findParentGroup([child], targetId);
                if (found) return found;
              }
            }
          }
        }
        return null;
      };

      const currentParentId = findParentGroup(this.groups, group.id);

      this.newGroupDialog = {
        visible: true,
        name: group.name,
        question: null,
        parentGroup: currentParentId,
        editingGroup: group
      };
    },

    // 删除分组
    deleteGroup(group) {
      this.$confirm('确定要删除该分组吗？删除后，该分组下的问题将变为未分组状态。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 递归删除分组
        const deleteGroupRecursive = (groups) => {
          const index = groups.findIndex(g => g.id === group.id);
          if (index !== -1) {
            // 将该分组及其所有子分组下的问题设置为未分组
            const clearGroupFromQuestions = (groupToClear) => {
              // 清空当前分组下的问题
              this.report.questions.forEach(q => {
                if (q.group === groupToClear.id) {
                  q.group = "";
                }
              });
              // 递归清空子分组下的问题
              if (groupToClear.children && groupToClear.children.length > 0) {
                groupToClear.children.forEach(childGroup => {
                  clearGroupFromQuestions(childGroup);
                });
              }
            };
            
            // 清空分组下的所有问题
            clearGroupFromQuestions(group);
            
            // 从父分组中移除当前分组
            groups.splice(index, 1);
            return true;
          }
          for (let g of groups) {
            if (g.children && g.children.length > 0) {
              if (deleteGroupRecursive(g.children)) {
                return true;
              }
            }
          }
          return false;
        };
        
        deleteGroupRecursive(this.groups);
        this.$message.success('删除成功');
      }).catch(() => {});
    },

    // 修改确认新建分组方法
    confirmNewGroup() {
      if (!this.newGroupDialog.name.trim()) {
        this.$message.warning("请输入分组名称");
        return;
      }

      // 如果是编辑现有分组
      if (this.newGroupDialog.editingGroup) {
        const editingGroup = this.newGroupDialog.editingGroup;
        
        // 如果父分组发生变化，需要先从原位置移除
        if (this.newGroupDialog.parentGroup !== null) {
          const removeFromOldParent = (groups) => {
            for (const group of groups) {
              if (group.children && group.children.length > 0) {
                const index = group.children.findIndex(g => g.id === editingGroup.id);
                if (index !== -1) {
                  group.children.splice(index, 1);
                  return true;
                }
                if (removeFromOldParent(group.children)) {
                  return true;
                }
              }
            }
            return false;
          };

          // 从根级别移除
          const rootIndex = this.groups.findIndex(g => g.id === editingGroup.id);
          if (rootIndex !== -1) {
            this.groups.splice(rootIndex, 1);
          } else {
            removeFromOldParent(this.groups);
          }

          // 添加到新的父分组下
          const addToNewParent = (groups) => {
            for (const group of groups) {
              if (group.id === this.newGroupDialog.parentGroup) {
                if (!group.children) {
                  group.children = [];
                }
                group.children.push(editingGroup);
                return true;
              }
              if (group.children && group.children.length > 0) {
                if (addToNewParent(group.children)) {
                  return true;
                }
              }
            }
            return false;
          };
          
          if (!addToNewParent(this.groups)) {
            // 如果找不到新父分组，添加到根级别
            this.groups.push(editingGroup);
          }
        } else {
          // 如果没有选择父分组，移动到根级别
          const removeFromParent = (groups) => {
            for (const group of groups) {
              if (group.children && group.children.length > 0) {
                const index = group.children.findIndex(g => g.id === editingGroup.id);
                if (index !== -1) {
                  group.children.splice(index, 1);
                  return true;
                }
                if (removeFromParent(group.children)) {
                  return true;
                }
              }
            }
            return false;
          };

          // 如果不在根级别，需要先移除
          const rootIndex = this.groups.findIndex(g => g.id === editingGroup.id);
          if (rootIndex === -1) {
            removeFromParent(this.groups);
            this.groups.push(editingGroup);
          }
        }

        // 更新分组名称
        editingGroup.name = this.newGroupDialog.name;
      } else {
        // 创建新分组的逻辑保持不变
        const newGroup = {
          id: Date.now().toString(),
          name: this.newGroupDialog.name,
          children: []
        };

        if (this.newGroupDialog.parentGroup) {
          const addToParent = (groups) => {
            for (let group of groups) {
              if (group.id === this.newGroupDialog.parentGroup) {
                if (!group.children) {
                  group.children = [];
                }
                group.children.push(newGroup);
                return true;
              }
              if (group.children && group.children.length > 0) {
                if (addToParent(group.children)) {
                  return true;
                }
              }
            }
            return false;
          };
          
          if (!addToParent(this.groups)) {
            this.groups.push(newGroup);
          }
        } else {
          this.groups.push(newGroup);
        }

        // 如果是通过问题选择器创建的分组，设置问题所属分组
        if (this.newGroupDialog.question) {
          this.newGroupDialog.question.group = newGroup.id;
        }
      }

      // 重置对话框
      this.newGroupDialog = {
        visible: false,
        name: "",
        question: null,
        parentGroup: null,
        editingGroup: null
      };
    },

    // 取消新建分组
    cancelNewGroup() {
      if (this.newGroupDialog.question) {
        this.newGroupDialog.question.group = "";
      }
      this.newGroupDialog = {
        visible: false,
        name: "",
        question: null,
        parentGroup: null,
        editingGroup: null
      };
    },

    // 显示新建行配置对话框
    showNewRowDialog() {
      this.newRowDialog = {
        visible: true,
        name: "",
        parentRow: null,
        editingRow: null,
        type: "data",
        calculationRule: "sum",
      };
    },

    // 添加子行
    addSubRow(parentRow) {
      this.newRowDialog = {
        visible: true,
        name: "",
        parentRow: parentRow.id,
        editingRow: null,
        type: "data",
        calculationRule: "sum",
      };
    },

    // 编辑行
    editRow(row) {
      this.newRowDialog = {
        visible: true,
        name: row.name,
        parentRow: null,
        editingRow: row,
        type: row.type || "data",
        calculationRule: row.calculationRule || "sum",
      };
    },

    // 删除行
    deleteRow(row) {
      this.$confirm('确定要删除该行配置吗？删除后，其子行也将被删除。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const deleteRowRecursive = (rows) => {
          const index = rows.findIndex(r => r.id === row.id);
          if (index !== -1) {
            rows.splice(index, 1);
            return true;
          }
          for (let r of rows) {
            if (r.children && r.children.length > 0) {
              if (deleteRowRecursive(r.children)) {
                return true;
              }
            }
          }
          return false;
        };
        
        deleteRowRecursive(this.rowConfigs);
        this.$message.success('删除成功');
      }).catch(() => {});
    },

    // 获取行配置选项
    getRowOptions() {
      const options = [];
      const buildOptions = (rows, prefix = '') => {
        rows.forEach(row => {
          const label = prefix ? `${prefix} / ${row.name}` : row.name;
          options.push({
            label,
            value: row.id
          });
          if (row.children && row.children.length > 0) {
            buildOptions(row.children, label);
          }
        });
      };
      buildOptions(this.rowConfigs);
      return options;
    },

    // 确认新建/编辑行配置
    confirmNewRow() {
      if (!this.newRowDialog.name.trim()) {
        this.$message.warning("请输入行名称");
        return;
      }

      // 如果是编辑现有行
      if (this.newRowDialog.editingRow) {
        this.newRowDialog.editingRow.name = this.newRowDialog.name;
        this.newRowDialog.editingRow.type = this.newRowDialog.type;
        this.newRowDialog.editingRow.calculationRule = this.newRowDialog.calculationRule;
      } else {
        // 创建新行配置
        const newRow = {
          id: Date.now().toString(),
          name: this.newRowDialog.name,
          type: this.newRowDialog.type,
          calculationRule: this.newRowDialog.calculationRule,
          children: []
        };

        // 如果有父行，添加到父行的children中
        if (this.newRowDialog.parentRow) {
          const addToParent = (rows) => {
            for (let row of rows) {
              if (row.id === this.newRowDialog.parentRow) {
                row.children.push(newRow);
                return true;
              }
              if (row.children && row.children.length > 0) {
                if (addToParent(row.children)) {
                  return true;
                }
              }
            }
            return false;
          };
          addToParent(this.rowConfigs);
        } else {
          // 否则添加到根级别
          this.rowConfigs.push(newRow);
        }
      }

      // 重置对话框
      this.newRowDialog = {
        visible: false,
        name: "",
        parentRow: null,
        editingRow: null,
        type: "data",
        calculationRule: "sum",
      };
    },

    // 取消新建/编辑行配置
    cancelNewRow() {
      this.newRowDialog = {
        visible: false,
        name: "",
        parentRow: null,
        editingRow: null,
        type: "data",
        calculationRule: "sum",
      };
    },

    // 添加计算字段
    addCalculationField(questionIndex) {
      const question = this.report.questions[questionIndex];
      
      // 如果是旧格式，先转换为新格式
      if (question.calculation && !Array.isArray(question.calculation.fields)) {
        question.calculation = {
          fields: [question.calculation.field1, question.calculation.field2],
          operators: [question.calculation.operator]
        };
      }

      // 如果没有calculation对象，初始化它
      if (!question.calculation) {
        question.calculation = {
          fields: [null, null],
          operators: [null]
        };
        return;
      }

      // 确保数组存在
      if (!Array.isArray(question.calculation.fields)) {
        question.calculation.fields = [null, null];
      }
      if (!Array.isArray(question.calculation.operators)) {
        question.calculation.operators = [null];
      }

      // 添加新的字段和运算符
      const newFields = [...question.calculation.fields, null];
      const newOperators = [...question.calculation.operators, '+'];
      
      // 更新数组
      question.calculation.fields = newFields;
      question.calculation.operators = newOperators;
    },

    // 移除计算字段
    removeCalculationField(questionIndex, fieldIndex) {
      const question = this.report.questions[questionIndex];
      
      // 如果是旧格式，先转换为新格式
      if (question.calculation && !Array.isArray(question.calculation.fields)) {
        question.calculation = {
          fields: [question.calculation.field1, question.calculation.field2],
          operators: [question.calculation.operator]
        };
      }

      if (!question.calculation || !Array.isArray(question.calculation.fields)) {
        return;
      }

      // 确保至少保留两个字段
      if (question.calculation.fields.length <= 2) {
        return;
      }

      // 创建新数组并删除指定元素
      const newFields = [...question.calculation.fields];
      const newOperators = [...question.calculation.operators];
      
      newFields.splice(fieldIndex, 1);
      if (fieldIndex < newOperators.length) {
        newOperators.splice(fieldIndex, 1);
      }

      // 更新数组
      question.calculation.fields = newFields;
      question.calculation.operators = newOperators;
    },

    // 修改问题类型时初始化计算字段
    handleTypeChange(question) {
      if (question.type === 'calculation') {
        // 使用新的数组格式初始化计算字段
        question.calculation = {
          fields: [null, null],
          operators: [null]
        };
      }
    },

    // 获取部门列表
    async fetchSubDepartments() {
      try {
        const response = await getSubDepartments();
        this.sub_departments = response.data.rows || [];
      } catch (error) {
        this.$message.error("获取旗县列表失败: " + (error.message || error));
      }
    },
  },
  mounted() {
    this.loadTemplate();
    this.fetchSubDepartments(); // 获取部门列表
  },
};
</script>

<style scoped>
.report-editor {
  padding: 20px;
}

/* 面包屑导航样式 */
.breadcrumb {
  margin-bottom: 20px;
  font-size: 16px;
}

.editor-container {
  max-width: 800px;
  margin: 0 auto;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.left {
  flex: 1;
}

.template-name-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.template-name-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.template-name-input {
  width: 300px;
}

.report-title {
  margin-bottom: 20px;
  font-size: 30px;
  height: 40px;
  text-align: center;
  display: block;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

/* 去掉输入框边框并居中文字 */
.report-title :deep(.el-input__wrapper) {
  box-shadow: none !important;
  border-bottom: 1px solid #dcdfe6;
  border-radius: 0;
  text-align: center;
  font-size: 30px;
  height: 40px;
}

.question-content :deep(.el-input__wrapper) {
  box-shadow: none !important;
  border-radius: 0;
  font-size: 16px;
  padding: 0px;
}

/* 错误样式 */
.error-field :deep(.el-input__wrapper),
.error-field :deep(.el-select__wrapper),
.error-option :deep(.el-input__wrapper) {
  box-shadow: 0 0 0 1px #f56c6c inset !important;
}
.error-field {
  margin-bottom: 0px !important;
}

.error-message {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 4px;
}
.report-title :deep(.el-input__inner) {
  text-align: center;
  color: #000000; /* 设置文字颜色为黑色 */
}

/* 报表描述样式 */
.report-description {
  margin-bottom: 20px;
  font-size: 16px;
  display: block;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
}

.question-item {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.question-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.question-number {
  font-weight: bold;
  min-width: 30px;
  font-size: 20px;
}

.question-type-container {
  display: flex;
  align-items: center;
}

.question-type {
  width: 120px;
  margin-right: 10px;
}

.question-group {
  width: 120px;
  margin-right: 10px;
}

.delete-question-btn {
  padding: 0;
  margin-left: 10px;
}

.question-content {
  margin-bottom: 10px;
}

.options-area {
  margin-top: 10px;
}

.option-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.option-item .el-input {
  margin-right: 10px;
}

.action-buttons {
  margin-top: 20px;
  text-align: center;
}

/* 智能计算区域样式 */
.calculation-area {
  margin-top: 15px;
}

.calculation-settings {
  padding: 10px;
  background-color: #f8f8f8;
  border-radius: 4px;
}

.calculation-result {
  margin-top: 15px;
}

/* 校验选项样式 */
.validation-options {
  margin-top: 15px;
  padding: 10px;
  background-color: #f8f8f8;
  border-radius: 4px;
}

.validation-item {
  margin-bottom: 15px;
}

.validation-item:last-child {
  margin-bottom: 0;
}

.number-input-area {
  margin-top: 10px;
}

.validation-options {
  margin-top: 10px;
}

.validation-item {
  margin-bottom: 10px;
}

.report-type-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.report-type-label {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.report-type-select {
  width: 200px;
}

/* 分组管理区域样式 */
.group-management {
  margin: 20px auto;
  max-width: 500px;
  padding: 20px;
  background-color: #f8f8f8;
  border-radius: 4px;
}

.group-management-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.group-management-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.group-tree {
  background-color: #fff;
  border-radius: 4px;
  padding: 10px;
}

.group-tree-node {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.group-tree-actions {
  display: none;
}

.group-tree-node:hover .group-tree-actions {
  display: flex;
  gap: 5px;
}

.group-tree-actions .el-button {
  padding: 2px;
}

.group-tree-actions .el-icon {
  font-size: 14px;
}

/* 行配置区域样式 */
.row-configuration {
  margin: 20px auto;
  max-width: 500px;
  padding: 20px;
  background-color: #f8f8f8;
  border-radius: 4px;
}

.row-configuration-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.row-configuration-header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}

.row-tree {
  background-color: #fff;
  border-radius: 4px;
  padding: 10px;
}

.row-tree-node {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.row-tree-actions {
  display: none;
}

.row-tree-node:hover .row-tree-actions {
  display: flex;
  gap: 5px;
}

.row-tree-actions .el-button {
  padding: 2px;
}

.row-tree-actions .el-icon {
  font-size: 14px;
}

.calculation-expression {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.expression-item {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
